import React, { useEffect, useState } from "react";
import { Bot, Loader2, Save, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProspectingAdmin() {
  const [agents, setAgents] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fee, setFee] = useState("");
  const [perLead, setPerLead] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ag, cfg] = await Promise.all([api.adminListAgents(), api.prospectingConfig()]);
      setAgents(ag); setConfig(cfg); setFee(String(cfg.activation_fee)); setPerLead(String(cfg.per_lead_price));
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try { await api.updateProspectingConfig({ activation_fee: Number(fee), per_lead_price: Number(perLead) }); toast.success("Pricing updated"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (loading) return <div className="glass-panel rounded-2xl p-12 text-center text-zinc-500">Loading prospecting agents…</div>;

  return (
    <div className="space-y-6" data-testid="admin-prospecting">
      {/* Pricing */}
      <div className="glass-panel rounded-2xl p-6">
        <h3 className="font-bold text-white text-sm uppercase tracking-wider mb-4">Prospecting Pricing</h3>
        <div className="flex flex-wrap items-end gap-4">
          <div><label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">Activation Fee ($)</label><input value={fee} onChange={(e) => setFee(e.target.value)} type="number" className="px-3 py-2.5 rounded-lg dark-input text-sm w-40" data-testid="config-fee" /></div>
          <div><label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">Per Qualified Lead ($)</label><input value={perLead} onChange={(e) => setPerLead(e.target.value)} type="number" className="px-3 py-2.5 rounded-lg dark-input text-sm w-40" data-testid="config-perlead" /></div>
          <button onClick={saveConfig} disabled={saving} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60" data-testid="config-save"><Save size={15} className="mr-2" /> Save</button>
        </div>
      </div>

      {/* Agents */}
      <div className="glass-panel rounded-2xl p-6">
        <h3 className="font-bold text-white text-sm uppercase tracking-wider mb-4 flex items-center gap-2"><Bot size={16} className="text-primary-400" /> Customer Agents ({agents.length})</h3>
        {agents.length === 0 && <p className="text-zinc-600 text-sm">No customer agents yet.</p>}
        <div className="space-y-2">
          {agents.map((a) => (
            <div key={a.id} className="rounded-xl bg-black/30 border border-white/10 p-4 flex flex-wrap items-center gap-4" data-testid="admin-agent-row">
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-bold text-white">{a.name}</div>
                <div className="text-[11px] text-zinc-500">{a.client?.business_name || a.client?.email} · {a.from_email}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.status === "active" ? "bg-green-500/15 text-green-300" : a.status === "paused" ? "bg-yellow-500/15 text-yellow-300" : "bg-zinc-500/15 text-zinc-400"}`}>{a.status}</span>
              <div className="text-[11px] text-zinc-400 flex gap-3"><span>Prospects {a.prospect_count}</span><span>Contacted {a.stats?.contacted || 0}</span><span>Qualified {a.stats?.qualified || 0}</span></div>
              {a.needs_review > 0 && <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-amber-500/15 text-amber-300 flex items-center gap-1" data-testid="admin-needs-review"><AlertTriangle size={11} /> {a.needs_review} need review</span>}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-500 mt-4">Prospect replies flagged "Needs Review" also appear in your Inbox. Full manual takeover per-thread is coming in the next phase.</p>
      </div>
    </div>
  );
}
